import React from 'react';
import { actionCreators as toastActionsCreators } from 'react/api/actions/toastAlerts';
import {
  complienceStatusIcon as complianceStatusIcon
} from 'react/utils/stopDetailsPopupBase64Icons';

const DAY_IN_MILLISECONDS = 86400000;

export const SEVEN_DAYS_IN_MILLISECONDS = 604800000;

export const toFormat = (amount) => Number(amount.toFixed(2)).toLocaleString(undefined, {
  minimumFractionDigits: 2
});

export const toDollarFormat = (amount) => `$${toFormat(amount)}`;

export const diffInDays = (date) => {
  const dueDate = new Date(date.split('T')[0].replace(/-/g, '/'));
  const dateToday = new Date(new Date().toLocaleDateString('en-US'));
  const diffInMiliSeconds = dueDate - dateToday;

  return Math.round(diffInMiliSeconds / DAY_IN_MILLISECONDS);
};

export const invertDayAndMonth = (date) => {
  const dateArray = date
    .split('T')[0]
    .split('-')
    .reverse();
  return `${dateArray[0]}/${dateArray[1]}/${dateArray[2]}`;
};

export const replaceSizeAlias = (string) => {
  const { size1Alias, size2Alias, size3Alias } = Monet.Session.Configurations;
  return string.replace('{size1Alias}', size1Alias)
    .replace('{size2Alias}', size2Alias)
    .replace('{size3Alias}', size3Alias);
};

export const replaceSizeAliasAndType = (string) => {
  const { size1Alias, size2Alias, size3Alias } = Monet.Session.Configurations;
  const replaced = string.replace('{size1Alias}', size1Alias)
    .replace('{size2Alias}', size2Alias)
    .replace('{size3Alias}', size3Alias);
  const [, ...rest] = replaced.split(' ')
  return rest.join(' ');
};

export const showAdequateDifferenceInTime = (time) => {
  const dayLabel = Monet.I18n.getResourcesByKey('Greenmile.UnitMetrics.dayAbreviation');
  const hourLabel = Monet.I18n.getResourcesByKey('Greenmile.UnitMetrics.hourAbreviation');
  const minLabel = Monet.I18n.getResourcesByKey('Greenmile.UnitMetrics.minuteAbreviation');
  const secLabel = Monet.I18n.getResourcesByKey('Greenmile.UnitMetrics.secondsAbreviation');

  const momentObj = moment.duration(Math.abs(time));
  const valueDays = momentObj.days();
  const valueHours = momentObj.hours();
  const valueMinutes = momentObj.minutes();
  const valueSeconds = momentObj.seconds();

  const days = valueDays > 0 ? `${valueDays}${dayLabel}` : '';
  const hours = valueHours > 0 ? `${valueHours}${hourLabel}` : '';
  const minutes = (valueHours > 0 || valueMinutes > 0) ? `${valueMinutes}${minLabel}` : '';
  const seconds = valueSeconds > 0 ? `${valueSeconds}${secLabel}` : '';

  let timeString = '';
  timeString += days ? `${days}` : '';
  timeString += hours ? ` ${hours}` : '';
  timeString += minutes ? ` ${minutes}` : '';
  timeString += seconds ? ` ${seconds}` : '';

  return timeString.trimStart();
};

export const checkPermissions = (type) => {
  const { permissions } = Monet.Session.Security;
  const hasPermission = permissions.find(
    (el) => {
      if (el.key === type) {
        return true;
      }
    }
  );
  return !!hasPermission;
};

/**
 * Checks if the user has any of the specified permissions
 * @param {Array<string>} permissionKeys - Array of permission keys to check
 * @returns {boolean} - True if the user has any of the permissions, false otherwise
 */
export const hasAnyPermission = (permissionKeys) => {
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
    return false;
  }
  
  return permissionKeys.some((key) => checkPermissions(key));
};

export const formatUnitSystem = (meters = 0) => {
  const { unitSystem } = Monet.Session.User;
  const kmAbbreviation = Monet.I18n.getResourcesByKey('UnitSystems.Kilometers.abbreviation');
  const miAbbreviation = Monet.I18n.getResourcesByKey('UnitSystems.Miles.abbreviation');
  const mAbbreviation = Monet.I18n.getResourcesByKey('UnitSystems.Meters.abbreviation');
  const distance = new Monet.Format.DISTANCE(meters);
  const base = 1000;
  const isMetric = unitSystem === 'METRIC';
  const has1Km = isMetric && meters >= base;

  const valueInMeters = has1Km && distance.convertMetersToKilometers().format()
  || isMetric && distance.format()
  || distance.convertMetersToMiles().format();

  const abbreviation = has1Km && kmAbbreviation || isMetric && mAbbreviation || miAbbreviation;

  return `${valueInMeters.getValue()} ${abbreviation}`;
};

export const fixGridAndFilterBuilder = () => {
  Object.keys(App.Forms).forEach((key) => {
    const form = App.Forms[key];

    if (form) {
      const grid = form.table;
      const { filterBuilder } = form;
      if (filterBuilder && filterBuilder?.adjustHeaderSize) {
        filterBuilder.adjustHeaderSize();
      }
      if (grid) {
        grid.scrollRowToTop(0);
        grid.resizeCanvas();
        grid.invalidate();
      }
    }
  });
}

export const isValidJSON = (string) => {
  try {
    return JSON.parse(string);
  } catch (error) {
    return false;
  }
};

/**
 * Cria um testador de Includes para um Texto especifico,
 * na função que é retornada, é passada a Lista de Textos para serem verificados
 * se existem algum, caso positivo retorna True.
 * @param {String} [textToTest]
 * @returns {Function} Essa função espera um @param {Array} [listOfTextsToCompare]
 * que @returns {Boolean} [True] se alguma parte do texto testado existir na lista
 */
export const createIncludesTestTo = (textToTest) => (listOfTextsToCompare) => listOfTextsToCompare.some((text) => textToTest.includes(text));

export const getProperty = (object, property, defaultValue) => {
  if (!object) {
    return defaultValue;
  }
  if (!property) {
    return object;
  }
  if (Array.isArray(property)) {
    let result = object;
    for (let i = 0; i < property.length; i++) {
      if (result.getIn) {
        return result.getIn(property.slice(i), defaultValue);
      }
      if (result[property[i]] === undefined) {
        return defaultValue;
      }
      result = result[property[i]];
    }
    return result;
  }
  if (object.get) {
    return object.get(property, defaultValue);
  }
  return object[property] !== undefined ? object[property] : defaultValue;
};

export const replaceAttributeWithValue = (text, state) => {
  if (text.includes('@order.totalPrice@')) {
    state = calculateTotalPrice(state);
  }
  return text.replace(/\@(.*?)\@/g, (match, defaultValue) => {
    const splitVariable = defaultValue.split('.');
    return getProperty(state, splitVariable, defaultValue);
  });
};

export const calculateTotalPrice = (state) => {
  let totalPrice = 0;
  const lineItems = state.get('order').get('lineItems');
  lineItems.forEach((lineItem) => totalPrice += lineItem.get('totalPrice'));
  const newState = new Map(state);
  newState.delete('order');
  const mapOrder = new Map(state.get('order')).set('totalPrice', totalPrice);
  newState.set('order', new Immutable.Map(mapOrder));
  return new Immutable.Map(newState);
};

export const getResourceTo = (resource, valueInResource) => Monet.I18n.getResourcesByKey(resource, valueInResource);


export const formatDate = (date) => Monet.Format.DateTime.formatToLocale(date);
export const formatDateOnlyMonth = (date) => Monet.Format.DateTime.formatToLocale(date, { dayMonthOnly: true });
export const formatDateISO = (date) => Monet.Format.DateTime.parseLocaleToISOString(date);

export const checkIfDataExist = (dataItem) => dataItem ?? '-';

export const checkIfResourceExist = (resourceName) => resourceName !== getResourceTo(resourceName)

export const toastNotification = {
  addToast({
    id,
    type,
    duration = 3000,
    data,
    customElementType,
    variant = 'default'
  }) {
    window.GreenmileReduxStore.dispatch(toastActionsCreators.addToast({
      id,
      type,
      duration,
      data,
      customElementType,
      variant
    }));
  },

  removeToast({ id }) {
    window.GreenmileReduxStore.dispatch(toastActionsCreators.removeToast({
      id
    }));
  }
};

export const generateLinkToDownloadCSV = ({ fileName, data, callback }) => {
  const parsedData = new Blob([data], { type: 'text/csv;encoding:utf-8' });

  const isInternetExplorer = navigator.userAgent.indexOf('MSIE') !== -1 ||
    navigator.userAgent.indexOf('Trident') !== -1;
  const link = document.createElement('a');

  document.body.appendChild(link);

  if (isInternetExplorer) {
    if (navigator.userAgent.indexOf('MSIE 9.0') > 0) {
      const newBrowserWindow = window.open('about:blank', '_blank', 'noopener,noreferrer');
      newBrowserWindow.document.write(parsedData);
      newBrowserWindow.document.close();
      newBrowserWindow.document.execCommand('SaveAs', true, fileName);
      newBrowserWindow.close();
      document.body.removeChild(link);
    } else {
      link.addEventListener('click', () => {
        navigator.msSaveBlob(parsedData, fileName);
        document.body.removeChild(link);
      }, false);
    }
  } else {
    link.href = URL.createObjectURL(parsedData);
    link.download = fileName;
    document.body.removeChild(link);
  }

  link.click();

  callback && callback();
}

export function formatConfidenceFilter(filterObj) {
  const formattedFilters = {...filterObj };
  formattedFilters.criteriaChain[0].and.forEach((item) => {
    if (item) {
      const { attr, gte, lte } = item;

      if (attr === 'confidence') {
        if (gte) {
          item.gte =
            (parseFloat(gte) / 100).toString() ?? undefined;
        }

        if (lte) {
          item.lte =
            (parseFloat(lte) / 100).toString() ?? undefined;
        }
      }
    }
  });

  return formattedFilters;
}

export function renderTransportInfo(transport) {
  const resource = getResourceTo("route.data.transport");

  return transport ? (
    <div>
      {`${resource} ${transport.alternativeKey}`}
    </div>
  ) : null;
}

export const validIfServerVersionIsBiggerThan = (lastUnsuportedVersion) => {
  const serverVersion = App.Version.GMSVersionName?.split('-')[0] || [
    '@Version@'
  ];
  return serverVersion.includes('@Version@') || Number(serverVersion) > lastUnsuportedVersion;
}

export const validIfBuildNumberIsBiggerThan = (lastUnsuportedBuildNumber) => {
  const serverVerssionArray = App.Version.GMSVersionName?.split('-')  || [
    '@Version@'
  ];
  const serverBuildNumber = serverVerssionArray.at(-1);
  return serverBuildNumber.includes('@Version@') || Number(serverBuildNumber) > lastUnsuportedBuildNumber;
}

export const validIfServerVersionIsLowerThan = (lastSuportedVersion) => {
  const serverVersion = App.Version.GMSVersionName?.split('-')[0] || [
    '@Version@'
  ];
  return (
    serverVersion.includes('@Version@') ||
    Number(serverVersion) < lastSuportedVersion
  );
};

export const validIfBuildNumberIsLowerThan = (lastSupportedBuildNumber) => {
  const serverVerssionArray = App.Version.GMSVersionName?.split('-') || [
    '@Version@'
  ];
  const serverBuildNumber = serverVerssionArray.at(-1);
  return (
    serverBuildNumber.includes('@Version@') ||
    Number(serverBuildNumber) < lastSupportedBuildNumber
  );
};
export const checkStopStatus = (stop) => {

  let stopStatus = 'onTime';
  let tw = null;

  const verifyOverrideTW = (locationOverrideTimeWindows) => {
    const tw = locationOverrideTimeWindows[0];

    return !tw.tw1Open ? false : true;
  };

  const checkOffDay = (stop) => {

    const weekday = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const referenceTime = stop.actualArrival || stop.projectedArrival;

    const dayNumber = new Date(referenceTime).getDay();

    let tw = null;

    if (stop.location && stop.location.locationOverrideTimeWindows) {
      tw = stop.location.locationOverrideTimeWindows;
    } else if (stop.location && stop.location.timeWindowType && stop.location.timeWindowType.timeWindowTypeDays) {
      tw = stop.location.timeWindowType.timeWindowTypeDays;
    }

    let customerTw = [];
    let offDay = true;

    if (tw) {
        for (var i = 0, len = tw.length; i < len; i++) {
            if (tw[i][weekday[dayNumber]]) {
                offDay = false;
                break;
            }
        }
    } else {
      if (stop.location && stop.location.deliveryDays) {
        customerTw = stop.location.deliveryDays.split(',');
        if (customerTw) {
          for (var z = 0, l = customerTw.length; z < l; z++) {
            if (parseInt(customerTw[z]) === dayNumber) {
              offDay = false;
              break;
            }
          }
        }
      }
    }

    return offDay;
  };

  if (stop.location && stop.location.locationOverrideTimeWindows && verifyOverrideTW(stop.location.locationOverrideTimeWindows)) {
    tw = stop.location.locationOverrideTimeWindows;
  } else if (stop.location && stop.location.timeWindowType && stop.location.timeWindowType.timeWindowTypeDays) {
    tw = stop.location.timeWindowType.timeWindowTypeDays;
  }

  let timeToCheck;
  let offDay = checkOffDay(stop);

  let projectedTimePart = 0;
  if (stop.projectedArrival) {
    projectedTimePart = Monet.Format.Time.formatToLocale(stop.projectedArrival);
  }

  let aArrivalTimePart = 0;
  if (stop.actualArrival) {
    aArrivalTimePart = Monet.Format.Time.formatToLocale(stop.actualArrival);
  }

  let pArrivalTimePart = 0;
  if (stop.plannedArrival) {
    pArrivalTimePart = Monet.Format.Time.formatToLocale(stop.plannedArrival);
  }

  if (!stop.actualArrival) {
    timeToCheck = projectedTimePart;
  } else {
    timeToCheck = aArrivalTimePart;
  }

  if (stop.cancelCode) {
    stopStatus = 'canceled';
  } else if (tw && timeToCheck) {
      for (let time = 0, l = tw.length; time < l; time++) {
        let timeWindow = tw[time];
        let timeToCheckMilli = Monet.Format.Time.parse(timeToCheck).getTime();

        let tw1OpenInMilli = 0;
        if (timeWindow.tw1Open) {
          tw1OpenInMilli = Monet.Format.Time.parse(timeWindow.tw1Open).getTime();
        }

        let tw1CloseInMilli = 0;
        if (timeWindow.tw1Close) {
          tw1CloseInMilli = Monet.Format.Time.parse(timeWindow.tw1Close).getTime();
        }

        let tw2OpenInMilli = 0;
        if (timeWindow.tw2Open) {
          tw2OpenInMilli = Monet.Format.Time.parse(timeWindow.tw2Open).getTime();
        }

        let tw2CloseInMilli = 0;
        if (timeWindow.tw2Close) {
          tw2CloseInMilli = Monet.Format.Time.parse(timeWindow.tw2Close).getTime();
        }

        let pArrivalTimePartMilli = Monet.Format.Time.parse(pArrivalTimePart).getTime();

        if ((tw1OpenInMilli <= timeToCheckMilli && timeToCheckMilli <= tw1CloseInMilli) || (tw2OpenInMilli <= timeToCheckMilli && timeToCheckMilli <= tw2CloseInMilli)) {
          stopStatus = 'onTime';

          if (offDay) {
            stopStatus = 'onTimeOffDay';
          }

          break;
        } else if (timeToCheckMilli <= tw1OpenInMilli || (timeToCheckMilli >= tw1CloseInMilli && timeToCheckMilli <= tw2OpenInMilli && timeToCheckMilli <= pArrivalTimePartMilli)) {
          stopStatus = 'early';
          if (offDay) {
            stopStatus = 'earlyOffDay';
          }
          break;
        } else if (timeToCheckMilli >= tw1OpenInMilli || timeToCheckMilli >= tw2CloseInMilli) {
          stopStatus = 'late';

          if (offDay) {
            stopStatus = 'lateOffDay';
          }
          break;
        }
      }
  }

  return stopStatus;
};

export const checkServiceStatus = (stop) => stop.actualDeparture ? 'Done' : 'Pending';

export const getDeliveryComplianceByStatusClass = (statusClass) => {
  const deliveryComplianceResources = {
    late: {
      resource: 'Stop.popup.deliveryCompliance.late',
      icon: complianceStatusIcon.LATE,
    },
    lateOffDay: {
      resource: 'Stop.popup.deliveryCompliance.late',
      icon: complianceStatusIcon.LATE,
    },
    onTime: {
      resource: 'Stop.popup.deliveryCompliance.onTime',
      icon: complianceStatusIcon.ONTIME,
    },
    onTimeOffDay: {
      resource: 'Stop.popup.deliveryCompliance.onTime',
      icon: complianceStatusIcon.ONTIME,
    },
    early: {
      resource: 'Stop.popup.deliveryCompliance.early',
      icon: complianceStatusIcon.EARLY,
    },
    earlyOffDay: {
      resource: 'Stop.popup.deliveryCompliance.early',
      icon: complianceStatusIcon.EARLY,
    },
  };

  return deliveryComplianceResources[statusClass];
};

export const getTWComplianceByStatusClass = (statusClass) => {
  const withInOnTime = statusClass.includes('onTime');
  const twStatus = withInOnTime ? 'within' : 'outside';

  const twComplianceResources = {
    outside: {
      resource: 'Stop.popup.twCompliance.outSide',
      icon: complianceStatusIcon.LATE,
    },
    within: {
      resource: 'Stop.popup.twCompliance.withIn',
      icon: complianceStatusIcon.ONTIME,
    },
  };

  const { icon, resource } = twComplianceResources[twStatus];


  return {
    icon,
    resource,
    twStatus
  }
};


export const getIsBlockedComplianceByStatues = ({ status, statusClass }) => {
  const blockedStatus = ['PENDING', 'CANCELED'];
  const blockedStatusClasses = ['canceled'];

  return blockedStatus.includes(status)
    || blockedStatusClasses.includes(statusClass)
    || !statusClass;
}

export const isValidLogin = (login = '') => {
  const regex = /^[a-zA-Z0-9@._-]+$/;
  return regex.test(login);
}

export const openUrlFile = (urlFile = '') => {
  const link = document.createElement('a');
  link.href = urlFile;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
